import { HubConnectionState } from '@microsoft/signalr';
import { ComponentProps, useCallback, useMemo, useRef, useState } from 'react';
import { Modal as RSModal } from 'reactstrap';
import { BulkEditStatus } from 'src/components/common/BulkActions/BulkEditStatus';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import {
  BulkEditStage,
  useBulkEditHubContext,
} from 'src/contexts/BulkEditHubContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { useContent } from 'src/contexts/ContentContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { vars } from 'src/core/themes';
import { Stack, Switch } from 'src/core/ui';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { useSearchV2DataRefetch } from 'src/hooks/useSearchV2DataRefetch';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  BulkActionType,
  BulkEditPreviewWithDetails,
  BulkEditProgress,
  BulkEditStep,
  Feature,
} from 'src/WebApiController';

import { BulkEditFooter } from '../common/BulkEditFooter';
import { BulkEditHeader } from '../common/BulkEditHeader';

export type BulkMergeSelectedListingsDialogProps = ComponentProps<
  typeof RSModal
> & {
  updateKey: string;
  onOkay: (
    supportBackgroundProcess?: boolean,
    onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
    preview?: BulkEditPreviewWithDetails,
    excludeHeldListings?: boolean
  ) => void;
  onCancel: () => void;
};

export function BulkMergeSelectedListingsDialog({
  updateKey,
  onOkay,
  onCancel,
  ...rest
}: BulkMergeSelectedListingsDialogProps) {
  const entityType = ActionOutboxEntityType.Listing;

  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { setSelectionMode } = useMultiSelectionContext();
  const { setProgress, setPreview, setMainDialogOpened } =
    useBulkEditHubContext();
  const { searchV2DataDelayedRefetch } = useSearchV2DataRefetch();
  const [isLoading, setIsLoading] = useState(false);

  const hasDefaultNotToMergeHoldListingsFeature = useUserHasFeature(
    Feature.DefaultNotToMergeHoldListings
  );

  const hasEnableOfficeFeature = useUserHasFeature(Feature.EnableOffice);
  const officeMismatchMsg = useContent(
    ContentId.MergeListings_BulkStorefrontMismatchWarning
  );

  const [excludeHeldListings, setExcludeHeldListings] = useState<boolean>(
    hasDefaultNotToMergeHoldListingsFeature
  );

  const onClose = useCallback(
    async (
      newProgress?: BulkEditProgress,
      closeMainDialogForPopover?: boolean
    ) => {
      if (newProgress?.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        setActivePosEntity(0);

        await searchV2DataDelayedRefetch();

        if (refreshExpandedListItems) {
          await refreshExpandedListItems();
        }
        setSelectionMode(undefined);
        if (closeMainDialogForPopover) {
          setMainDialogOpened(false);
        } else {
          setProgress(undefined);
          setPreview(undefined);
        }
        setIsRefreshing(false);
        // progress kept to allow the BulkEditStatusPopover to show the result
      }
      onCancel();
    },
    [
      onCancel,
      refreshExpandedListItems,
      searchV2DataDelayedRefetch,
      setActivePosEntity,
      setMainDialogOpened,
      setPreview,
      setProgress,
      setSelectionMode,
    ]
  );

  const onBulkEditDone = useCallback(
    async (doneProgress: BulkEditProgress, finalErrors: string[]) => {
      if (finalErrors.length === 0 && doneProgress.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        onClose(doneProgress, false);
      }
    },
    [onClose]
  );

  const { bulkEditHub, progress, preview, initJob, stage } = useBulkEditHub(
    entityType,
    BulkActionType.MergeSelectedListings,
    updateKey,
    onBulkEditDone
  );

  const supportBackgroundProcess = useMemo(
    () => bulkEditHub?.state === HubConnectionState.Connected,
    [bulkEditHub?.state]
  );

  const onSubmitHandler = useCallback(
    (onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        onOkay(false, onPreviewReceived);
      } else {
        onOkay(
          supportBackgroundProcess,
          undefined,
          preview,
          excludeHeldListings
        );
      }
      if (!supportBackgroundProcess) {
        setIsLoading(false);
      }
    },
    [onOkay, supportBackgroundProcess, preview, excludeHeldListings]
  );

  const [isRefreshing, setIsRefreshing] = useState(false);

  const submitButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <GenericDialog
      {...rest}
      size={stage === BulkEditStage.Preview ? 'xl' : 'md'}
      header={
        <BulkEditHeader headerText={<Content id={ContentId.MergeSelected} />} />
      }
      onOpened={initJob}
      onClick={(e) => {
        // This so that this dialog does not cause event to flow to the EventHeader open/close event
        e.stopPropagation();
        e.preventDefault();
      }}
      onClosed={() => {
        setMainDialogOpened(false);
        if (progress) {
          setSelectionMode(undefined);
        }
        // Call the outside one if there is one
        rest.onClosed?.();
      }}
      footer={
        isRefreshing ? null : (
          <BulkEditFooter
            entityType={entityType}
            isLoading={isLoading}
            hasBackgroundBulkEditFeature={supportBackgroundProcess}
            onClose={onClose}
            onSubmit={onSubmitHandler}
            submittButtonRef={submitButtonRef}
          />
        )
      }
      onCancel={isLoading ? undefined : onCancel}
    >
      <BulkEditStatus
        entityType={entityType}
        isLoading={isRefreshing || isLoading}
        updateKey={updateKey}
      >
        <Stack direction="column" width="full" gap="m">
          <Content id={ContentId.MergeSelectedListingsConfirmation} />

          {hasEnableOfficeFeature && (
            <WarningMessage message={officeMismatchMsg} />
          )}

          {hasDefaultNotToMergeHoldListingsFeature && (
            <Stack
              direction="row"
              gap="s"
              alignItems="center"
              style={{
                fontSize: vars.typography.fontSize.base,
                fontWeight: 500,
              }}
            >
              <Switch
                checked={!excludeHeldListings}
                onChange={(e) => e.stopPropagation()}
                onCheckedChange={(isChecked) => {
                  if (isChecked === excludeHeldListings) {
                    setExcludeHeldListings(!isChecked);
                  }
                }}
              />
              <Content id={ContentId.IncludeHeldListings} />
            </Stack>
          )}
        </Stack>
      </BulkEditStatus>
    </GenericDialog>
  );
}
